import { getCsrfToken } from '@/api/getCsrfTokenRequest.util.ts';
import CONFIG from '@/config/config.ts';
import { getAppLanguage } from '@/i18n.ts';
import axios, { AxiosResponse, HttpStatusCode } from 'axios';
import { useRef } from 'react';

import { HTTP_HEADER } from './httpHeaders.model';
import { HttpMethod } from './HttpMethod.model';

const useSuccessfulResponseHandler = () => {
  return (response: AxiosResponse): AxiosResponse => {
    if (response.status === HttpStatusCode.Found) {
      delete response.headers[HTTP_HEADER['X-CSRF-TOKEN']];
    }

    return response;
  };
};

const useErrorResponseHandler = () => {
  return (error: unknown) => {
    if (
      axios.isAxiosError(error) &&
      (error.code === 'ERR_NETWORK' || error.response?.status === HttpStatusCode.ServiceUnavailable)
    ) {
      if (window.location.pathname !== '/503') {
        window.location.href = '/503';
      }
      return Promise.reject(error);
    }
    return Promise.reject(error);
  };
};

type Return = ReturnType<typeof axios.create>;

export const useAxiosInstance = (): Return => {
  const isErrorInterceptorAddedRef = useRef(false);
  const successfulResponseHandler = useSuccessfulResponseHandler();
  const errorResponseHandler = useErrorResponseHandler();

  const axiosInstance = axios.create();

  if (!isErrorInterceptorAddedRef.current) {
    isErrorInterceptorAddedRef.current = true;
    axiosInstance.interceptors.response.use(successfulResponseHandler, errorResponseHandler);
  }

  axiosInstance.interceptors.request.use(async config => {
    // Replaces the browser's own Accept-Language, so the backend follows the app language, not the browser
    config.headers[HTTP_HEADER['Accept-Language']] = getAppLanguage();
    if (
      config.method &&
      [HttpMethod.PUT, HttpMethod.POST, HttpMethod.PATCH, HttpMethod.DELETE].some(
        method => method === config.method,
      )
    ) {
      config.headers[HTTP_HEADER['X-CSRF-TOKEN']] = await getCsrfToken();
    } else {
      delete config.headers[HTTP_HEADER['X-CSRF-TOKEN']];
    }
    if (CONFIG.ENVIRONMENT === 'local') {
      config.headers['x-auth-token'] = `${CONFIG.JSESSION}`;
    }
    return config;
  });

  return axiosInstance;
};
